import { performPostRequest } from '@atlassian/jira-fetch/src/index.tsx';
import type {
	IssueTypeConfigMap,
	ResponseData,
	UseLazyIssueTypeFieldsArguments,
} from './types.tsx';
import { ISSUE_TYPES_AND_FIELDS_BOARD_QUERY } from './gql.tsx';

/**
 * This function is responsible for fetching the issuework types and its associated fields for a given project key.
 * */
export const getIssueTypesAndFields = ({
	projectKey,
}: UseLazyIssueTypeFieldsArguments): Promise<IssueTypeConfigMap> => {
	return performPostRequest('/rest/gira/1/?operation=JiraBusinessProjectDetails', {
		body: JSON.stringify({
			query: ISSUE_TYPES_AND_FIELDS_BOARD_QUERY,
			variables: {
				projectKey,
				issueOperation: 'CREATE',
				first: 100,
			},
		}),
	}).then(responseToIssueIdFieldsMap);
};
/**
 * This function maps the response data to a more usable format and passes it to the provided setter.
 * @param response - The response data from the GraphQL query.
 * @returns A new object where the keys are the issue/work type IDs and the values are objects containing the issue/work type data and fields.
 * The resulting object is then passed to the setter function.
 */
export const responseToIssueIdFieldsMap = (response: ResponseData): IssueTypeConfigMap => {
	const resultingMap: IssueTypeConfigMap = {};
	response.data.jiraBusinessAvailableFields.edges.forEach(({ node: typeNode }) => {
		const { issueTypeId } = typeNode.issueType;
		resultingMap[issueTypeId] = {
			...typeNode.issueType,
			fields: typeNode.fields.edges.map(({ node: fieldNode }) => ({
				...fieldNode,
				fieldId: fieldNode.fieldId,
				type: fieldNode.type,
				name: fieldNode.name,
				aliasFieldId: fieldNode.aliasFieldId,
				fieldConfig: {
					isRequired: fieldNode.fieldConfig?.isRequired,
					isEditable: fieldNode.fieldConfig?.isEditable,
				},
			})),
		};
	});
	return resultingMap;
};
